'use client';

import { useTranslations } from 'next-intl';
import { useRouter, useSearchParams } from 'next/navigation';
import React, { useEffect, useMemo, useState } from 'react';
import {
  AirRingColors,
  FrontendBackendRingNameMap,
  productMap_air_ring,
  getRingAirColorNameImages,
  getAvailableAirRingColors,
  isRingAirSkuDisabled,
  RingSizes as RingSizesEnum,
  RingType,
} from 'content/products/ring';
import { CustomImage as Image } from 'components/shared';
import { API_BASE_URL } from 'lib/config/apiEndpoints';
import { emailRegex } from 'utils/data';
import { useGlobalUI } from 'contexts/GlobalUIContext';
import { useRegionLocale } from 'contexts/RegionLocaleContext';
import CustomLink from 'components/shared/CustomLink';
import { InfoIcon } from 'assets/icons';
import { ShopifyStore } from 'lib/commerce/shopify/shopifyHelper';
import { CurrencySymbol } from 'components/shared/CurrencySymbol';
import { Currency } from 'utils/Currency';
import {
  getAvailableRingProColors,
  isRingProSkuDisabled,
  RING_PRO_COLOR_BACKEND_ID,
  RingProColors,
  ringProColorFromBackendId,
} from 'content/products/ring-pro/constants';
import { RingProVariants } from 'content/products/ring-pro/ringProVariants';
import {
  parseRingPurchaseCodeResponse,
  resolveRingClaimUi,
  ringClaimModelGate,
  type RingPurchaseCodeApiResponse,
  type RingPurchaseCodeSession,
} from 'utils/ring-claim/purchaseCode';
import styles from './Form.module.css';

const RingSizes = [5, 6, 7, 8, 9, 10, 11, 12, 13, 14];

interface Country {
  id: number;
  name: string;
  iso2: string;
  country_code: string;
  phone_code: string;
}

interface State {
  id: number;
  name: string;
  iso2: string;
  state_code: string;
}

interface City {
  id: number;
  name: string;
  iso2: string;
}

export const DisclaimerText = () => {
  const t = useTranslations('common');
  return (
    <p className={styles.disclaimerTextStyle}>
      <InfoIcon />
      <span>
        {t('form.text.exploreOtherColour')}{' '}
        <CustomLink href="mailto:support@ultrahuman.com">
          support@ultrahuman.com
        </CustomLink>
      </span>
    </p>
  );
};

export const RoseGoldRefundDisclaimerText = () => {
  const t = useTranslations('common');
  return (
    <p className={styles.disclaimerTextStyle}>
      {t('form.disclaimerTextStyle.ifYouReturn')}
    </p>
  );
};
const upgradePrice: Record<ShopifyStore, number> = {
  [ShopifyStore.IN]: 5500,
  [ShopifyStore.AE]: 190,
  [ShopifyStore.ROW]: 50,
  [ShopifyStore.US]: 50,
  [ShopifyStore.GB]: 50,
  [ShopifyStore.EU]: 50,
  [ShopifyStore.AU]: 80,
  [ShopifyStore.CA]: 70,
  [ShopifyStore.SA]: 219,
  [ShopifyStore.MX]: 1780,
  [ShopifyStore.ZA]: 1000,
  [ShopifyStore.JP]: 8980,
};

export const RoseGoldDisclaimerText = ({ store }: { store: ShopifyStore }) => {
  const t = useTranslations('common');
  const { currency } = useRegionLocale();
  return (
    <p className={styles.disclaimerTextCaptionStyle}>
      {t('form.disclaimerTextCaptionStyle.upgradeBrushedRose')}{' '}
      {CurrencySymbol[(currency ?? Currency.usd) as Currency]}
      {upgradePrice[store]}
    </p>
  );
};

export type RingClaimColorCatalog = Record<
  string,
  { image: string; name: string }
>;

export const Form = ({
  className,
  defaultColorByPage,
  product = 'ring_air',
  colorCatalog,
}: {
  className?: string;
  defaultColorByPage?: AirRingColors;
  /** AIR is the live claim page. PRO is /ring-pro/claim. */
  product?: 'ring_air' | 'ring_pro';
  /** Required for ring_pro. AIR builds its own color images. */
  colorCatalog?: RingClaimColorCatalog;
}) => {
  const t = useTranslations('common');
  const tRing = useTranslations('ring');
  const RingAirColorNameImages = getRingAirColorNameImages((key: string) =>
    tRing(key),
  );
  useGlobalUI({ liteUI: true });
  const router = useRouter();
  const searchParams = useSearchParams();
  const code = searchParams?.get('code');
  const [inputToken, setInputToken] = useState<string>('');
  const [countries, setCountries] = useState<Country[]>([]);
  const [states, setStates] = useState<State[]>([]);
  const [cities, setCities] = useState<City[]>([]);

  const [userToken, setUserToken] = useState<string>('');
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [phone, setPhone] = useState<string>();
  const [email, setEmail] = useState<string>();
  const [address1, setAddress1] = useState('');
  const [address2, setAddress2] = useState('');
  const [city, setCity] = useState<string>();
  const [cityTextValue, setCityTextValue] = useState<string>();
  const [postalCode, setPostalCode] = useState<string>();
  const [state, setState] = useState<State | 'other'>();
  const [stateTextValue, setStateTextValue] = useState<string | undefined>();
  const [country, setCountry] = useState<Country>();
  const [size, setSize] = useState<number>();
  const [color, setColor] = useState<string>();
  const [wrongProductHref, setWrongProductHref] = useState<string | null>(null);
  const isPro = product === 'ring_pro';
  const [colorSelectionDisabled, setColorSelectionDisabled] =
    useState<boolean>(false);

  const { region: ipCountry, regionSlug } = useRegionLocale();
  const [purchaseCodeSession, setPurchaseCodeSession] =
    useState<RingPurchaseCodeSession | null>(null);

  const [isLoading, setLoading] = useState<boolean>(false);

  const ErrorStates: Record<string, boolean> = {
    firstName: false,
    lastName: false,
    phone: false,
    email: false,
    address1: false,
    address2: false,
    postalCode: false,
    state: false,
    city: false,
    country: false,
    size: false,
    color: false,
  };

  const [error, setErrors] = useState<typeof ErrorStates>(ErrorStates);
  const [formError, setFormError] = useState<boolean>(false);
  const [tokenErrorMessage, setTokenErrorMessage] = useState<string | null>(
    null,
  );

  const validateForm = () => {
    let submitActive = true;
    const temp = { ...ErrorStates };

    if (!firstName) {
      temp.firstName = true;
      submitActive = false;
    }
    if (!lastName) {
      temp.lastName = true;
      submitActive = false;
    }
    if (!phone || Number(phone) == 0 || String(phone).length <= 5) {
      temp.phone = true;
      submitActive = false;
    }
    if (!email || !email.match(emailRegex)) {
      temp.email = true;
      submitActive = false;
    }
    if (!address1) {
      temp.address1 = true;
      submitActive = false;
    }
    if (!address2) {
      temp.address2 = true;
      submitActive = false;
    }
    if (!postalCode) {
      temp.postalCode = true;
      submitActive = false;
    }
    if (!state || (state === 'other' && !stateTextValue)) {
      temp.state = true;
      submitActive = false;
    }
    if (!city || (city === 'other' && !cityTextValue)) {
      temp.city = true;
      submitActive = false;
    }
    if (!country) {
      temp.country = true;
      submitActive = false;
    }
    if (!size) {
      temp.size = true;
      submitActive = false;
    }
    if (!color) {
      temp.color = true;
      submitActive = false;
    }

    setErrors(temp);

    return submitActive;
  };

  useEffect(() => {
    if (code) {
      setInputToken(code);
    }
  }, [code]);

  useEffect(() => {
    fetchCountries();
  }, []);

  const fetchCountries = async () => {
    try {
      const response = await fetch('/api/geo/countries.json');
      const data = await response.json();
      console.log({ data });
      // AIR claim cannot ship to US, UM, or PR. PRO claim lists every country.
      const filteredData = isPro
        ? data
        : data.filter(
            (country: any) =>
              country.iso2 !== 'US' &&
              country.iso2 !== 'UM' &&
              country.iso2 !== 'PR',
          );
      setCountries(filteredData);
    } catch (error) {
      console.error('Error fetching countries:', error);
    }
  };

  const fetchStates = async (countryId: string) => {
    try {
      const response = await fetch(`/api/geo/states/${countryId}.json`);
      const data = await response.json();
      setStates(data);
    } catch (error) {
      console.error('Error fetching states:', error);
    }
  };

  const fetchCities = async (stateId: string) => {
    try {
      const response = await fetch(`/api/geo/cities/${stateId}.json`);
      const data = await response.json();
      setCities(data);
    } catch (error) {
      console.error('Error fetching cities:', error);
    }
  };

  const handleCountryChange = (event: React.ChangeEvent<HTMLSelectElement>) => {
    const selectedOption = event.target.value;

    const selectedCountry = countries.find(
      (country) => country.name === selectedOption,
    );
    if (selectedCountry) {
      setCountry(selectedCountry);
      setState(undefined);
      setStateTextValue(undefined);
      setCity(undefined);
      setCityTextValue(undefined);
    }
    // Fetch states based on the selected country
    fetchStates(selectedCountry?.iso2 as string);
  };

  const handleStateChange = (event: React.ChangeEvent<HTMLSelectElement>) => {
    const selectedOption = event.target.value;

    if (selectedOption === 'other') {
      setState('other');
      setStateTextValue(undefined);
      setCity(undefined);
      setCityTextValue(undefined);
      return;
    }

    const selectedState = states.find((state) => state.name === selectedOption);
    setState(selectedState);
    setCity(undefined);
    setCityTextValue(undefined);

    // Fetch cities based on the selected state
    fetchCities(selectedState?.state_code as string);
  };

  const handleCityChange = (event: React.ChangeEvent<HTMLSelectElement>) => {
    const selectedCityId = event.target.value;
    setCity(selectedCityId);
  };

  const verifyToken = async (token: string) => {
    try {
      const response = await fetch(
        `${API_BASE_URL}/api/web_v1/ring_purchase_code`,
        {
          method: 'POST',
          body: JSON.stringify({ purchase_code: token }),
        },
      );
      const data = (await response.json()) as RingPurchaseCodeApiResponse;
      const session = parseRingPurchaseCodeResponse(data, token, {
        ipCountry,
        defaultColorByPage,
        fromBackendColor: isPro ? ringProColorFromBackendId : undefined,
      });

      // PRO opens only for an exact `ring_pro`. AIR treats a missing or
      // unexpected value as AIR, and rejects an exact `ring_pro`.
      const gate = ringClaimModelGate(
        data.ring_model,
        isPro ? 'ring_pro' : 'air',
      );
      if (gate.kind === 'stop') {
        setPurchaseCodeSession(null);
        setWrongProductHref(gate.wrongProductHref);
        setTokenErrorMessage(
          gate.error === 'purchaseCodeForAir'
            ? t('form.error.purchaseCodeForAir')
            : gate.error === 'purchaseCodeForPro'
              ? t('form.error.purchaseCodeForPro')
              : data.message || t('form.error.somethingWentWrong'),
        );
        return;
      }

      if (session) {
        setWrongProductHref(null);
        setPurchaseCodeSession(session);
        setUserToken(token);
        setTokenErrorMessage(null);
        setColor(session.defaultColor ?? undefined);
        setColorSelectionDisabled(session.colorSelectionDisabled);
      } else {
        setPurchaseCodeSession(null);
        setTokenErrorMessage(
          data.message || t('form.error.somethingWentWrong'),
        );
      }
      // console.log({ data });
    } catch (error) {
      console.error('Error fetching states:', error);
      setTokenErrorMessage(t('form.error.somethingWentWrong'));
    }
  };

  const handleSubmit = async (event: React.FormEvent) => {
    event.preventDefault();
    setTokenErrorMessage(null); // Reset token error message on submit
    setLoading(true);
    const formValidationPass = validateForm();
    if (formValidationPass === false) {
      setLoading(false);
      setFormError(true);
      return;
    }

    const bodyData = {
      email,
      size,
      color: isPro
        ? RING_PRO_COLOR_BACKEND_ID[color as RingProColors]
        : FrontendBackendRingNameMap.get(color as string),
      purchase_code: userToken,
      phone_number: phone,
      shipping_address: {
        city: state === 'other' || city === 'other' ? cityTextValue : city,
        name: `${firstName} ${lastName}`,
        country: country?.name,
        address1,
        address2,
        postal_code: postalCode,
        state: state !== 'other' ? (state as State).name : stateTextValue,
        country_code: country?.iso2,
      },
    };

    try {
      const response = await fetch(`${API_BASE_URL}/api/web_v1/ring_order`, {
        method: 'POST',
        body: JSON.stringify(bodyData),
      });
      const data = await response.json();
      if (data.success) {
        if (data && 'redirect_to' in data && data.redirect_to) {
          router.push(data.redirect_to);
          setLoading(false);
          return;
        }
        router.push(
          `/${regionSlug}/${
            isPro ? 'ring-pro/claim' : 'ring/claim'
          }/status?status=success`,
        );
        setLoading(false);
      } else {
        setTokenErrorMessage(
          data.message || t('form.error.somethingWentWrong'),
        );
      }
    } catch (error) {
      console.error('Error fetching states:', error);
      setTokenErrorMessage(t('form.error.somethingWentWrong'));
      setLoading(false);
    } finally {
      setLoading(false);
    }
  };

  const disabledSkus = purchaseCodeSession?.disabledSkus ?? [];

  const claimUi = useMemo(
    () =>
      purchaseCodeSession
        ? resolveRingClaimUi(purchaseCodeSession, color)
        : null,
    [purchaseCodeSession, color],
  );

  // Filter out colors that are disabled based on API response and the SKU map.
  // brg_only purchase codes can still pick ROSE_GOLD so existing claims complete.
  const availableColors = useMemo(() => {
    if (isPro) {
      const allowed = purchaseCodeSession?.availableColorIds;
      return getAvailableRingProColors(purchaseCodeSession?.store).filter(
        (colorOption) => {
          const backendColorName = RING_PRO_COLOR_BACKEND_ID[colorOption];
          if (disabledSkus.includes(backendColorName)) return false;
          if (
            allowed &&
            allowed.length > 0 &&
            !allowed.includes(backendColorName)
          ) {
            return false;
          }
          return true;
        },
      );
    }

    const store = purchaseCodeSession?.store;
    const colors = [...getAvailableAirRingColors(store)];
    if (
      purchaseCodeSession?.colorFlow === 'brg_only' &&
      !colors.includes(AirRingColors.ROSE_GOLD)
    ) {
      colors.push(AirRingColors.ROSE_GOLD);
    }
    return colors.filter((colorOption) => {
      const backendColorName = FrontendBackendRingNameMap.get(colorOption);
      return !backendColorName || !disabledSkus.includes(backendColorName);
    });
  }, [
    disabledSkus,
    isPro,
    purchaseCodeSession?.availableColorIds,
    purchaseCodeSession?.store,
    purchaseCodeSession?.colorFlow,
  ]);

  // Reset color selection if current color becomes disabled
  useEffect(() => {
    if (color && disabledSkus.length > 0) {
      const backendColorName = isPro
        ? RING_PRO_COLOR_BACKEND_ID[color as RingProColors]
        : FrontendBackendRingNameMap.get(color);
      if (backendColorName && disabledSkus.includes(backendColorName)) {
        setColor(undefined);
      }
    }
  }, [disabledSkus, color, isPro]);

  const colorView = (colorOption: string) => {
    if (isPro) {
      const row = colorCatalog?.[colorOption];
      return { image: row?.image ?? '', name: row?.name ?? colorOption };
    }
    const row = RingAirColorNameImages[colorOption as AirRingColors];
    return {
      image: (row?.image as string) ?? '',
      name: String(row?.name ?? colorOption),
    };
  };

  const backendColorOf = (colorOption: string) =>
    isPro
      ? RING_PRO_COLOR_BACKEND_ID[colorOption as RingProColors]
      : (FrontendBackendRingNameMap.get(colorOption) as string | undefined);

  const sizeIsDisabled = (sizeOption: number) => {
    if (isPro) {
      const proColor = color as RingProColors | undefined;
      const proSize = sizeOption as RingSizesEnum;
      return (
        isRingProSkuDisabled({
          color: proColor,
          size: proSize,
          type: RingType.NOT_ENGRAVED,
          store: purchaseCodeSession?.store,
        }) ||
        disabledSkus.includes(
          (proColor
            ? RingProVariants[proColor]?.[proSize]?.[RingType.NOT_ENGRAVED]?.SKU
            : undefined) as string,
        )
      );
    }

    return (
      (purchaseCodeSession?.colorFlow === 'brg_only' &&
      color === AirRingColors.ROSE_GOLD
        ? isRingAirSkuDisabled({
            size: sizeOption as RingSizesEnum,
            store: purchaseCodeSession?.store,
          })
        : isRingAirSkuDisabled({
            color: color as AirRingColors,
            size: sizeOption as RingSizesEnum,
            type: RingType.NOT_ENGRAVED,
            store: purchaseCodeSession?.store,
          })) ||
      disabledSkus.includes(
        productMap_air_ring[color as AirRingColors]?.[
          sizeOption as RingSizesEnum
        ]?.NOT_ENGRAVED.SKU as string,
      )
    );
  };

  const tokenErrorNode = tokenErrorMessage ? (
    <>
      {tokenErrorMessage}{' '}
      {wrongProductHref ? (
        <CustomLink href={wrongProductHref}>
          {t(
            wrongProductHref === '/ring-pro/claim'
              ? 'form.text.claimOnPro'
              : 'form.text.claimOnAir',
          )}
        </CustomLink>
      ) : null}
    </>
  ) : null;

  return (
    <div className={className ? `${styles.root} ${className}` : styles.root}>
      {userToken ? (
        <form
          className={!userToken || !!tokenErrorMessage ? styles.disabled : ''}
        >
          <div className={styles.form}>
            <div className={styles['text-description']}>
              <h1>
                {t(
                  isPro
                    ? 'form.title.completeYourOrderPro'
                    : 'form.title.completeYourOrder',
                )}
              </h1>
              <p className={styles.titles}>{t('form.text.enterYourRing')}</p>
            </div>
            <div className={`contact-details ${styles.block}`}>
              <p className={styles.titles}>{t('form.text.contactDetails')}</p>
              <div className={styles['input-rows']}>
                <div className={styles.row}>
                  <label>
                    <span>
                      {t('form.text.firstName')} <b>*</b>
                    </span>
                    <input
                      style={{
                        borderColor: error.firstName ? 'red' : undefined,
                      }}
                      type="text"
                      name="firstName"
                      placeholder={t('form.input.placeholder.requiredField')}
                      required
                      value={firstName}
                      onChange={(e) => {
                        setFirstName(e.target.value);
                        setFormError(false);
                      }}
                      disabled={isLoading}
                    />
                  </label>
                  <label>
                    <span>
                      {t('form.text.lastName')} <b>*</b>
                    </span>
                    <input
                      style={{
                        borderColor: error.lastName ? 'red' : undefined,
                      }}
                      type="text"
                      name="lastName"
                      placeholder={t('form.input.placeholder.requiredField')}
                      required
                      value={lastName}
                      onChange={(e) => {
                        setLastName(e.target.value);
                        setFormError(false);
                      }}
                      disabled={isLoading}
                    />
                  </label>
                </div>
                <label>
                  <span>
                    {t('form.text.phoneNumber')} <b>*</b>
                  </span>
                  <input
                    type="tel"
                    style={{
                      borderColor: error.phone ? 'red' : undefined,
                    }}
                    inputMode="numeric"
                    name="phonenumber"
                    placeholder={t(
                      'form.input.placeholder.phoneNumberRequired',
                    )}
                    required
                    value={phone}
                    pattern="^\+?\d*$"
                    onChange={(e) => {
                      if (!e.target.value.match(/^\+?\d*$/)) return;
                      setPhone(e.target.value);
                      setFormError(false);
                    }}
                    disabled={isLoading}
                  />
                </label>
                <label>
                  <span>
                    {t('form.text.email')} <b>*</b>
                  </span>
                  <input
                    style={{
                      borderColor: error.email ? 'red' : undefined,
                    }}
                    type="email"
                    name="email"
                    placeholder={t('form.input.placeholder.emailRequired')}
                    required
                    value={email}
                    onChange={(e) => {
                      setEmail(e.target.value);
                      setFormError(false);
                    }}
                    disabled={isLoading}
                  />
                </label>
              </div>
            </div>
            <div className={`shipping-details ${styles.block}`}>
              <p className={styles.titles}>{t('form.text.shippingDetails')}</p>
              {purchaseCodeSession?.shippingDisclaimer ? (
                <p className={styles.disclaimer}>
                  {purchaseCodeSession.shippingDisclaimer}
                </p>
              ) : null}

              <div className={styles['input-rows']}>
                <div className={styles['select-wrapper']}>
                  <label>
                    <span>
                      {t('form.text.country')} <b>*</b>
                    </span>
                    <select
                      style={{
                        borderColor: error.country ? 'red' : undefined,
                      }}
                      id="country"
                      value={country?.name || ''}
                      onChange={(e) => {
                        handleCountryChange(e);
                        setFormError(false);
                      }}
                      required
                      disabled={isLoading}
                    >
                      <option value="">
                        {t('form.content.selectCountry')}
                      </option>
                      {countries.map((country) => (
                        <option key={country.iso2} value={country.name}>
                          {country.name}
                        </option>
                      ))}
                    </select>
                  </label>
                </div>

                <label>
                  <span>
                    {t('form.text.addressLine')} <b>*</b>
                  </span>
                  <input
                    type="text"
                    style={{
                      borderColor: error.address1 ? 'red' : undefined,
                    }}
                    name="address1"
                    placeholder={t(
                      'form.input.placeholder.apartmentSuiteCompany',
                    )}
                    required
                    value={address1}
                    onChange={(e) => {
                      setAddress1(e.target.value);
                      setFormError(false);
                    }}
                    disabled={isLoading}
                  />
                </label>

                <label>
                  <span>
                    {t('form.text.addressLine2')} <b>*</b>
                  </span>
                  <input
                    style={{
                      borderColor: error.address2 ? 'red' : undefined,
                    }}
                    type="text"
                    name="address2"
                    placeholder={t('form.input.placeholder.addressEnterNa')}
                    required
                    value={address2}
                    onChange={(e) => {
                      setAddress2(e.target.value);
                      setFormError(false);
                    }}
                    disabled={isLoading}
                  />
                </label>

                <label>
                  <span>
                    {t('form.text.postalCode')} <b>*</b>
                  </span>
                  <input
                    style={{
                      borderColor: error.postalCode ? 'red' : undefined,
                    }}
                    type="text"
                    name="postalCode"
                    placeholder={t('form.input.placeholder.postalCodeRequired')}
                    required
                    value={postalCode}
                    onChange={(e) => {
                      setPostalCode(e.target.value);
                      setFormError(false);
                    }}
                    disabled={isLoading}
                  />
                </label>

                <div className={styles.row}>
                  <div
                    className={`${styles['select-wrapper']} ${
                      !country ? styles.disabled : ''
                    }`}
                  >
                    <label>
                      <span>
                        {t('form.text.state')} <b>*</b>
                      </span>
                      <select
                        style={{
                          borderColor: error.state ? 'red' : undefined,
                        }}
                        id="state"
                        value={state !== 'other' ? state?.name || '' : 'other'}
                        onChange={(e) => {
                          handleStateChange(e);
                          setFormError(false);
                        }}
                        disabled={!country || isLoading}
                        required
                      >
                        <option value="">
                          {t('form.content.selectState')}
                        </option>
                        {states.map((state) => (
                          <option key={state.id} value={state.name}>
                            {state.name}
                          </option>
                        ))}
                        <option key={'other'} value={'other'}>
                          {t('form.content.other')}
                        </option>
                      </select>
                    </label>
                  </div>

                  {state === 'other' ? (
                    <label>
                      <span>
                        {t('form.text.state')} <b>*</b>
                      </span>
                      <input
                        style={{
                          borderColor: error.state ? 'red' : undefined,
                        }}
                        type="text"
                        name="state"
                        placeholder={t('form.input.placeholder.enterState')}
                        required
                        value={stateTextValue ?? ''}
                        onChange={(e) => {
                          setStateTextValue(e.target.value);
                          setFormError(false);
                        }}
                        disabled={isLoading}
                      />
                    </label>
                  ) : null}
                </div>
                <div className={styles.row}>
                  {state !== 'other' ? (
                    <div
                      className={`${styles['select-wrapper']} ${
                        !(country && state) ? styles.disabled : ''
                      }`}
                    >
                      <label>
                        <span>
                          {t('form.text.city')} <b>*</b>
                        </span>
                        <select
                          style={{
                            borderColor: error.city ? 'red' : undefined,
                          }}
                          id="city"
                          value={city || ''}
                          onChange={(e) => {
                            handleCityChange(e);
                            setFormError(false);
                          }}
                          required
                          disabled={!(country && state) || isLoading}
                        >
                          <option value="">
                            {t('form.content.selectCity')}
                          </option>
                          {cities.map((city) => (
                            <option key={city.id} value={city.iso2}>
                              {city.name}
                            </option>
                          ))}
                          <option value="other">
                            {t('form.content.other')}
                          </option>
                        </select>
                      </label>
                    </div>
                  ) : null}

                  {city === 'other' || state === 'other' ? (
                    <label>
                      <span>
                        {t('form.text.city')} <b>*</b>
                      </span>
                      <input
                        style={{
                          borderColor: error.city ? 'red' : undefined,
                        }}
                        type="text"
                        name="city"
                        placeholder={t('form.input.placeholder.city')}
                        required
                        value={cityTextValue ?? ''}
                        onChange={(e) => {
                          setCityTextValue(e.target.value);
                          setFormError(false);
                        }}
                        disabled={isLoading}
                      />
                    </label>
                  ) : null}
                </div>
              </div>
            </div>

            <div
              className={`color-details ${styles.block} ${
                colorSelectionDisabled ? styles.disabled : ''
              }`}
            >
              {colorSelectionDisabled ? (
                <p className={styles.titles}>
                  {t('form.text.yourPreSelected')}
                </p>
              ) : (
                <p
                  className={styles.titles}
                  style={{
                    color: error.color ? 'red' : undefined,
                    textDecoration: error.color ? 'underline' : '',
                  }}
                >
                  {t('form.text.selectYourRing')}
                </p>
              )}

              <div className={styles.colors}>
                {availableColors.map((colorOption) => (
                  <button
                    disabled={
                      isLoading ||
                      (colorSelectionDisabled && color != colorOption)
                    }
                    type="button"
                    key={colorOption}
                    className={color == colorOption ? styles.active : ''}
                    onClick={() => {
                      setColor(colorOption);
                      setFormError(false);
                    }}
                  >
                    {!isPro &&
                    colorOption === AirRingColors.ROSE_GOLD &&
                    claimUi?.showRoseGoldNewBadge ? (
                      <div className={styles.tag}>{t('form.content.new')}</div>
                    ) : null}
                    <Image
                      width={82}
                      height={80}
                      src={colorView(colorOption).image}
                      alt={colorView(colorOption).name}
                    />
                    {colorView(colorOption).name}
                  </button>
                ))}
              </div>
              {!isPro &&
              !colorSelectionDisabled &&
              purchaseCodeSession?.store &&
              (availableColors as string[]).includes(
                AirRingColors.ROSE_GOLD,
              ) ? (
                <RoseGoldDisclaimerText store={purchaseCodeSession.store} />
              ) : null}
              {colorSelectionDisabled && !defaultColorByPage ? (
                <DisclaimerText />
              ) : null}
            </div>
            <div className={`sizing-details ${styles.block}`}>
              <p
                className={styles.titles}
                style={{
                  color: error.size ? 'red' : undefined,
                  textDecoration: error.size ? 'underline' : '',
                }}
              >
                {t('form.text.selectYourSize')}
              </p>
              <div className={styles.sizes}>
                {RingSizes.map((sizeOption) => (
                  <button
                    type="button"
                    key={sizeOption}
                    className={size == sizeOption ? styles.active : ''}
                    onClick={() => {
                      setSize(sizeOption);
                      setFormError(false);
                    }}
                    disabled={isLoading || sizeIsDisabled(sizeOption)}
                  >
                    {sizeOption}
                  </button>
                ))}
              </div>
              <div>
                {!isPro && claimUi?.showRoseGoldRefundDisclaimer ? (
                  <RoseGoldRefundDisclaimerText />
                ) : null}
              </div>

              {color &&
              purchaseCodeSession?.selectionDisclaimer &&
              purchaseCodeSession.selectionDisclaimerValidColors?.includes(
                backendColorOf(color) as string,
              ) ? (
                <p className={styles.disclaimer}>
                  {purchaseCodeSession.selectionDisclaimer}
                </p>
              ) : null}
            </div>
            <button
              className={styles.submit}
              type="submit"
              onClick={handleSubmit}
              disabled={isLoading || !!tokenErrorMessage || formError}
            >
              {isLoading
                ? t('form.button.placingOrder')
                : t(`form.button.${claimUi?.submitButtonKey ?? 'placeOrder'}`)}
            </button>
            <div className={styles['error-message']}>
              <p>
                {formError ? (
                  <>{t('form.text.pleaseReCheck')}</>
                ) : (
                  tokenErrorNode
                )}
              </p>
            </div>
          </div>
        </form>
      ) : (
        <div className={styles.form}>
          <div className={styles['text-description']}>
            <h1>
              {t(
                isPro
                  ? 'form.title.completeYourOrderPro'
                  : 'form.title.completeYourOrder2',
              )}
            </h1>
            <p className={styles.titles}>{t('form.text.enterYourRing')}</p>
          </div>
          <div className={`contact-details ${styles.block}`}>
            <p>{t('form.text.purchaseCode')}</p>
            <input
              type="text"
              name="userToken"
              placeholder={t('form.input.placeholder.enterPurchaseCode')}
              value={inputToken}
              onChange={(e) => {
                setInputToken(e.target.value);
                setTokenErrorMessage(null);
                setWrongProductHref(null);
              }}
              disabled={isLoading}
            />
            <div className={styles['error-message']}>
              {tokenErrorNode ? <p>{tokenErrorNode}</p> : null}
            </div>
          </div>
          <button
            className={styles.submit}
            disabled={!inputToken || !!tokenErrorMessage || isLoading}
            onClick={() => verifyToken(inputToken)}
          >
            {t('form.button.continue')}
          </button>
        </div>
      )}
    </div>
  );
};
